import { debounce } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { setChartSettingsTableSettingsAction } from '../../../redux/slices/analyses';
import { useAppDispatch } from '../../../redux/hooks';
import { useChartContext } from '../../Chart/ChartContext';
import { useDragResize } from '../../../utils/hooks/useDragResize';

const MIN_COLUMN_WIDTH = 120;
const RANK_CELL_WIDTH = 64;

interface IProps {
  analysisId: string;
  initialColumnWidths: number[];
  hasRankCell?: boolean;
}

export default ({
  analysisId,
  initialColumnWidths,
  hasRankCell = false,
}: IProps) => {
  const dispatch = useAppDispatch();
  const {
    settings: { chartSettings },
  } = useChartContext();
  const [columnWidths, setColumnWidths] = useState(
    chartSettings.tableSettings.columnWidths ?? initialColumnWidths,
  );
  const [multiGridWidth, setMultiGridWidth] = useState<number>();

  const getColumnWidth = useCallback(
    ({ index }) => {
      return columnWidths[index];
    },
    [columnWidths],
  );

  const onChangeColumnWidths = (newColWidths: number[]) => {
    setColumnWidths(newColWidths);
    dispatch(
      setChartSettingsTableSettingsAction({
        analysisId,
        tableSettings: {
          columnWidths: newColWidths,
        },
      }),
    );
  };

  const debouncedDispatch = useMemo(
    () =>
      debounce((newColWidths) => {
        dispatch(
          setChartSettingsTableSettingsAction({
            analysisId,
            tableSettings: {
              columnWidths: newColWidths,
            },
          }),
        );
      }, 300),
    [analysisId, dispatch],
  );

  const handleResizeProgress = useCallback(
    (e: MouseEvent, refIndex: number) => {
      const deltaX = e.movementX;
      setColumnWidths((prevWidths: number[]) => {
        const newWidths = [...prevWidths];

        const currentWidth = newWidths[refIndex];
        const newWidth = newWidths[refIndex] + deltaX;

        const draftWidths = [...newWidths];
        draftWidths[refIndex] = newWidth;

        const totalNewWidths = draftWidths.reduce((a, b) => a + b, 0);

        if (multiGridWidth && totalNewWidths + 8 < multiGridWidth) {
          newWidths[refIndex] = currentWidth;
        } else if (hasRankCell && refIndex === 0) {
          // rank cell is smaller in default max width than normal cells
          newWidths[refIndex] =
            newWidth >= RANK_CELL_WIDTH ? newWidth : RANK_CELL_WIDTH;
        } else {
          newWidths[refIndex] =
            newWidth >= MIN_COLUMN_WIDTH ? newWidth : MIN_COLUMN_WIDTH;
        }
        debouncedDispatch(newWidths);
        return newWidths;
      });
    },
    [debouncedDispatch, hasRankCell, multiGridWidth],
  );

  const { isResizing, handleResizeStart } = useDragResize({
    onResizeProgress: handleResizeProgress,
  });

  return {
    isResizing,
    columnWidths,
    getColumnWidth,
    handleMouseDown: handleResizeStart,
    onChangeColumnWidths,
    multiGridWidth,
    setMultiGridWidth,
  };
};
